import { useState, useContext } from 'react';
import axios from 'axios';
import { 
  TextField, 
  Box, 
  Typography, 
  InputAdornment,
  Alert,
  Collapse,
  IconButton,
  Stack
} from '@mui/material';
import AdminPanelSettingsIcon from '@mui/icons-material/Shield';
import LockIcon from '@mui/icons-material/Lock';
import BadgeIcon from '@mui/icons-material/Badge';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { UserContext } from './Context';
import { storeAdminSession } from './administration/adminApi';

function AdminLogIn() {
  const { setUser } = useContext(UserContext);
  const [matricule, setMatricule] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!/^\d{9}$/.test(matricule)) {
      setError('Le matricule doit comporter 9 chiffres. <br/> Het stamnummer moet uit 9 cijfers bestaan.');
      return;
    }

    try {
      const response = await axios.post('/api/adminauth/login', { matricule, password });
      const { token, expiresAt } = response.data;

      storeAdminSession(token, expiresAt);
      setUser('admin');
    } catch (err) {
      setPassword('');
      if (err.response && err.response.status === 429) {
        setError('Trop de tentatives. Réessayez plus tard. <br/> Te veel pogingen. Probeer het later opnieuw.');
      } else if (err.response && err.response.status === 401) {
        const attemptsLeft = err.response.data?.attemptsLeft;
        let message = 'Matricule ou mot de passe incorrect. <br/> Onjuist stamnummer of wachtwoord.';
        if (typeof attemptsLeft === 'number') {
          message += ` <br/> Tentatives restantes : ${attemptsLeft} / Resterende pogingen: ${attemptsLeft}`;
        }
        setError(message);
      } else {
        setError('Erreur de connexion. <br/> Verbindingsfout.');
      }
    }
  };

  return (
    <Box sx={{ mt: 1 }}>
      <Box 
        onClick={() => setExpanded(!expanded)} 
        sx={{ 
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ 
            display: 'flex', 
            alignItems: 'center',
            fontWeight: 500
          }}
        >
          <AdminPanelSettingsIcon 
            fontSize="small" 
            sx={{ mr: 1, opacity: 0.7 }} 
          />
          Accès administrateur / Beheerders­toegang
        </Typography>
        <IconButton 
          size="small"
          sx={{ 
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s'
          }}
        >
          <ExpandMoreIcon fontSize="small" />
        </IconButton>
      </Box>
      
      <Collapse in={expanded}>
        <Box 
          component="form" 
          onSubmit={handleSubmit}
          autoComplete="off"
          sx={{
            mt: 2
          }}
        >
          <Stack 
            direction="row" 
            spacing={1}
            alignItems="center"
          >
            <TextField
              id="admin_matricule"
              name="admin_matricule"
              label="Matricule / Stamnummer"
              size="small"
              variant="outlined"
              fullWidth
              value={matricule}
              onChange={(e) => setMatricule(e.target.value.replace(/\D/g, '').slice(0, 9))}
              inputProps={{ inputMode: 'numeric', maxLength: 9 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BadgeIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              id="admin_password"
              name="admin_password"
              label="Mot de passe / Wachtwoord"
              type="password"
              size="small"
              variant="outlined"
              fullWidth
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />
            
            <IconButton
              type="submit"
              color="primary"
              size="small"
              aria-label="Connexion / Aanmelden"
              title="Connexion / Aanmelden"
              disabled={!matricule || !password}
            >
              <ArrowForwardIcon />
            </IconButton>
          </Stack>
          
          {error && (
            <Alert severity="error" sx={{ mt: 1, py: 0, whiteSpace: 'pre-line' }}>
              {error.replace(/<br\s*\/?>(\s*)/g, '\n')}
            </Alert>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

export default AdminLogIn;
